import type { OnInit } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import type { FieldTree } from '@angular/forms/signals';
import type { WebLegacyWidgetEntity } from '@web/types';

import { ChangeDetectionStrategy, Component, ElementRef, computed, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';

import { EButtonStyleType, EButtonType, ESize } from '@common/enums';
import { socialIconTemplates } from '@common/icons/social-icons';

import type { ExtendedPositionLegacy, SocialEntity, SocialItem } from '@sites/entity-core';
import type { SocialForm } from '@sites/entity-signal-form';

import { getSocialNetworkIconSrc } from '@web/shared/components/web-social-networks/utils';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';

import { WebBaseViewSignalFormComponent } from '@web/widgets/common/abstract/web-base-view-signal-form.component';
import {
    BUTTON_SIZE_CLASS_MAP,
    BUTTON_TYPE_CLASS_MAP,
    DEFAULT_BUTTON_CLASS,
} from '@web/widgets/common/base/base-button/base-button.const';

import {
    getSocialGroupMargin,
    getSocialItemCssClasses,
    getSocialItemCssVars,
    type SocialCssVars,
} from '@sites/entity-core/src/widget/social/styles/social.css-vars';
import { flexAlignVar, rotateVar } from '@sites/entity-core';
import { colorCss } from '@web/widgets/common/css-vars/css-vars.utils';
import { afterFormValueChange } from '@web/widgets/common/zero-size-sync/after-form-value-change';
import { NewZeroWidgetsService } from '@web/widgets/common/zero-size-sync/new-zero-widgets.service';

/**
 * Editor preview for the `social-networks` widget — fully signal-based, the
 * social twin of the migrated button view. The form is shared with the
 * control panel via `WebLegacyWidgetSignalFormService` (ref-counted by widget id).
 * Styling is LESS + CSS custom properties (the css-vars module is shared with
 * the websites-builder renderer).
 */
@Component({
    selector: 'web-social-view',
    templateUrl: './social-view.component.html',
    styleUrls: ['./social-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [],
})
export class SocialViewComponent
    extends WebBaseViewSignalFormComponent<WebLegacyWidgetEntity<SocialEntity['data']>>
    implements OnInit
{
    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly newZeroWidgets: NewZeroWidgetsService = inject(NewZeroWidgetsService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly elementRef: ElementRef = inject(ElementRef);
    private readonly sanitizer: DomSanitizer = inject(DomSanitizer);

    public readonly EButtonType = EButtonType;

    public form!: SocialForm;

    public readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });
    public readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });

    /**
     * True when the widget lives inside a zero block. Read through `value()`
     * — NOT `form.type()`: the `type` FieldTree child only materialises when
     * the key exists in the model, and default-variant entities omit it.
     */
    public get isZero(): boolean {
        return this.form?.value().type === 'zero';
    }

    private readonly data = computed<SocialEntity['data']>(() => this.form.value().data);
    private readonly isZeroWidget = computed<boolean>(() => this.form.value().type === 'zero');

    private readonly position = computed(() => this.form.position?.().value());
    public readonly iconSize = computed<string>(() => `${this.form.height().value()}px`);

    // --- CSS custom property values (bound on the group wrapper in the template) ---
    public readonly groupZIndex = computed<string | null>(() => {
        const zIndex = this.position()?.zIndex;
        return zIndex != null ? `${zIndex}` : null;
    });
    public readonly groupZIndexMobile = computed<string | null>(() => {
        const position = this.position();
        const zIndex = this.form.isSynchronized() ? position?.zIndex : position?.mobileZIndex;
        return zIndex != null ? `${zIndex}` : null;
    });
    public readonly groupTransform = computed<string>(() =>
        this.isZeroWidget() ? 'none' : rotateVar(this.position()?.rotate)
    );
    public readonly groupTransformMobile = computed<string>(() =>
        this.isZeroWidget() ? 'none' : rotateVar(this.position()?.mobileRotate)
    );
    public readonly groupMargin = computed<string>(() => getSocialGroupMargin(this.data(), this.isZeroWidget()));
    public readonly groupJustify = computed<string>(() => flexAlignVar(this.form.horizontalAlign().value()));
    public readonly groupJustifyMobile = computed<string | null>(() => {
        const align = this.form.mobileHorizontalAlign().value();
        return align ? flexAlignVar(align) : null;
    });
    public readonly groupJustifyTablet = computed<string | null>(() => {
        const align = this.form.tabletHorizontalAlign().value();
        return align ? flexAlignVar(align) : null;
    });

    // Shared item styling — the widget renders one appearance for every entry.
    public readonly socialItemCssVars = computed<SocialCssVars>(() => getSocialItemCssVars(this.data(), colorCss));
    private readonly socialItemCssClasses = computed<string>(() => getSocialItemCssClasses(this.data()));

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<SocialForm>(this.widget().id, this.destroyRef);

        if (this.isZero) {
            this.setupZeroSizeSync();
        }
    }

    /**
     * Zero-block social widgets auto-size with their content: the rendered
     * entries' total width and height are mirrored back into the
     * extended-position grid so the zero host's selection frame and boundary
     * math track the real size. Replaces the FormGroup view's
     * `valueChanges → getComputedStyle → setWidth/setHeight` subscription
     * (with its `skipValueChange` re-entrancy guard) — here the
     * change-comparison in `setSize` breaks the feedback loop instead
     * (the button/text view `setupZeroHeightSync` pattern).
     */
    private setupZeroSizeSync(): void {
        afterFormValueChange(
            this.injector,
            () => this.form._raw().value(),
            () => this.syncZeroSizeFromDom(),
            { measureInitial: this.newZeroWidgets.consume(this.widget().id) }
        );
    }

    private syncZeroSizeFromDom(): void {
        const root = this.elementRef.nativeElement.firstElementChild as HTMLElement | null;
        if (!root) return;

        const style = getComputedStyle(root);

        this.setSize(parseFloat(style.width), parseFloat(style.height));
    }

    /**
     * Mirror the measured size into the extended-position grid — the active
     * device only, or all three on desktop.
     */
    private setSize(width: number, height: number): void {
        if (!Number.isFinite(width) || width <= 0) return;
        if (!Number.isFinite(height) || height <= 0) return;

        // The form's position accessor is union-typed (simple grid on default
        // placement) — this code path only runs for zero socials.
        const positionField = this.form.position as unknown as FieldTree<ExtendedPositionLegacy>;
        const position = positionField().value();

        const write = () => {
            if (this.isDeviceMobileSig()) {
                if (position.mobileWidth !== width) positionField.mobileWidth().value.set(width);
                if (position.mobileHeight !== height) positionField.mobileHeight().value.set(height);
                return;
            }

            if (this.isDeviceTabletSig()) {
                if (position.tabletWidth !== width) positionField.tabletWidth().value.set(width);
                if (position.tabletHeight !== height) positionField.tabletHeight().value.set(height);
                return;
            }

            if (position.mobileWidth !== width) positionField.mobileWidth().value.set(width);
            if (position.tabletWidth !== width) positionField.tabletWidth().value.set(width);
            if (position.width !== width) positionField.width().value.set(width);

            if (position.mobileHeight !== height) positionField.mobileHeight().value.set(height);
            if (position.tabletHeight !== height) positionField.tabletHeight().value.set(height);
            if (position.height !== height) positionField.height().value.set(height);
        };

        // Batch the writes into one store dispatch — unless an outer
        // transaction (settings panel editing) is already open: the plugin
        // doesn't support nesting, a nested `transact` would commit the outer
        // snapshot prematurely.
        if (this.form.inTransaction()) {
            write();
        } else {
            this.form.transact(write);
        }
    }

    public getStyleList(social: SocialItem): string {
        const styleType = this.form.styleType().value();

        if (social.type === EButtonType.icon) {
            return 'sp-social-item social-icon';
        }

        const base = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[this.form.buttonSize().value() ?? ESize.M]} sp-social-item`;
        const modifiers = this.socialItemCssClasses();

        if (styleType === EButtonStyleType.custom) {
            return `${base} ${modifiers}`;
        }

        return `${base} ${BUTTON_TYPE_CLASS_MAP[styleType as keyof typeof BUTTON_TYPE_CLASS_MAP]} ${modifiers}`;
    }

    // Bound in an `@for` loop and re-run every change-detection tick;
    // `bypassSecurityTrustHtml` returns a fresh `SafeHtml` each call, forcing a
    // `[innerHTML]` DOM rewrite. Cache by icon name so the reference stays stable.
    private readonly sanitizedIconCache = new Map<string, SafeHtml>();

    public getIcon(iconName: string): SafeHtml {
        const cached = this.sanitizedIconCache.get(iconName);
        if (cached !== undefined) {
            return cached;
        }

        const safe = this.sanitizer.bypassSecurityTrustHtml(socialIconTemplates[iconName]);
        this.sanitizedIconCache.set(iconName, safe);

        return safe;
    }

    public getSocialIconSrcBySocial(social: SocialItem): string {
        const folderName: string = social.type === EButtonType.button ? 'full-white' : social.iconType;
        return getSocialNetworkIconSrc(folderName, social.socialNetwork);
    }
}
